import { Box, Button, Icon, Theme, Typography } from "@outschool/backpack";
import { faBellConcierge } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { joinMembershipPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  useConcierge,
  useIntercomConciergeExperiment
} from "@outschool/ui-components-website";
import { useNavigation } from "@outschool/ui-utils";
import React, { useEffect, useState } from "react";

import ConciergeChatModal from "../chat/ConciergeChatModal";
import PageModal from "../PageModal";
import { ConciergeNavTab } from "./DesktopNavTabs";

interface ConciergeNavItemProps {
  minimalistic?: boolean;
  onClick?: () => void;
}

export const ConciergeNavItemDisplay = ({
  minimalistic = false,
  onClick
}: ConciergeNavItemProps) => {
  const { t } = useTranslation("client\\components\\nav\\ConciergeNavItem");
  const { isLoggedIn } = useSession();

  if (isLoggedIn) {
    return <ConciergeNavTab onClick={onClick} />;
  }

  return (
    <Box
      onClick={onClick}
      sx={(theme: Theme) => ({
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("md")]: {
          marginY: theme.spacing(10)
        }
      })}
    >
      <Button
        variant="link"
        sx={(theme: Theme) => ({
          padding: minimalistic ? "0px" : "10px 10px",
          border: "none",
          outline: "none",
          position: "relative",
          borderRadius: "30px",
          cursor: "pointer",
          zIndex: "1",
          color: "black",
          backgroundColor: "transparent",
          ":hover:not(:disabled)": {
            textDecoration: "none"
          },
          [theme.breakpoints.down("md")]: {
            display: "flex",
            flexDirection: "column-reverse",
            paddingY: theme.spacing(14),
            paddingX: minimalistic ? theme.spacing(5) : theme.spacing(20)
          }
        })}
      >
        <Typography
          variant="body2"
          sx={(theme: Theme) => ({
            [theme.breakpoints.up("sm")]: {
              display: "none"
            }
          })}
        >
          {t("Concierge")}
        </Typography>
        {!minimalistic && (
          <Icon
            icon={faBellConcierge}
            sx={{ fontSize: "2rem", marginRight: "5px" }}
          />
        )}
        <Typography
          emphasized
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("sm")]: {
              display: "none"
            }
          })}
        >
          {t("Concierge")}
        </Typography>
      </Button>
    </Box>
  );
};

export const ConciergeNavItemContainer = ({
  children
}: {
  children: (props: { onClick: () => void }) => React.ReactElement;
}) => {
  const { t } = useTranslation("client\\components\\nav\\ConciergeNavItem");
  const trackEvent = useTrackEvent();
  const { isLoggedIn } = useSession();
  const { canAccessConcierge } = useConcierge();
  const navigate = useNavigation();

  const [openCTAModal, setOpenCTAModal] = useState<boolean>(false);
  const [conciergeChatModalOpen, setConciergeChatModalOpen] =
    useState<boolean>(false);

  const { isIntercomConciergeTreatment, triggerExperiment } =
    useIntercomConciergeExperiment();
  const [shouldNavigateAfterClose, setShouldNavigateAfterClose] =
    useState(false);

  const openModal = () => {
    if (!canAccessConcierge) {
      setOpenCTAModal(true);
    }

    if (canAccessConcierge) {
      triggerExperiment();
    }

    if (canAccessConcierge && !isIntercomConciergeTreatment) {
      setConciergeChatModalOpen(true);
      trackEvent("concierge_modal_opened");
    }

    // if is treatment, will trigger intercom chat on intercom side.
    if (canAccessConcierge && isIntercomConciergeTreatment) {
      trackEvent("concierge_intercom_opened");
    }
  };

  const handleJoinClick = () => {
    trackEvent("concierge_join_membership_clicked");
    setShouldNavigateAfterClose(true);
    setOpenCTAModal(false);
  };

  useEffect(() => {
    if (!openCTAModal && shouldNavigateAfterClose) {
      navigate(joinMembershipPath());
      setShouldNavigateAfterClose(false);
    }
  }, [openCTAModal, shouldNavigateAfterClose, navigate]);

  const loggedinCopy = (
    <Trans t={t}>
      <Typography>Our</Typography>{" "}
      <Typography sx={{ fontWeight: "bold" }}>
        member-exclusive Concierge team
      </Typography>{" "}
      <Typography>is here to help.</Typography>
      <br />
      <Typography>
        Sign up for an Outschool membership and we’ll get back to you within 2
        business days with class suggestions tailored just for you.
      </Typography>
    </Trans>
  );
  const loggedoutCopy = (
    <Typography>
      {t(
        "With an Outschool membership, you’ll gain access to our member-exclusive Concierge team of real people."
      )}
      <br />
      {t(
        "Once you join, you can submit a personalized request and one of our experts will follow up with class recommendations tailored just for you."
      )}
    </Typography>
  );

  return (
    <>
      {children({ onClick: () => openModal() })}
      <ConciergeChatModal
        isOpen={conciergeChatModalOpen}
        setIsModalOpen={setConciergeChatModalOpen}
      />
      <PageModal
        open={openCTAModal}
        onClose={() => setOpenCTAModal(false)}
        fullBleed={false}
      >
        <Box sx={{ padding: 10 }}>
          <Typography variant="h1" sx={{ marginBottom: 20 }}>
            {t("Need help finding the right class?")}
          </Typography>
          {isLoggedIn ? loggedinCopy : loggedoutCopy}
          <br />
          <TrackedButton
            variant="contained"
            color="primary"
            onClick={handleJoinClick}
            sx={{
              marginTop: 20
            }}
            trackingName="join_to_access_concierge"
          >
            {t("Join to Access Concierge")}
          </TrackedButton>
        </Box>
      </PageModal>
    </>
  );
};

export function ConciergeNavItem({
  minimalistic = false
}: {
  minimalistic?: boolean;
}) {
  const trackEvent = useTrackEvent();
  const handleNavClick = () => {
    trackEvent("concierge_nav_item_clicked");
  };
  return (
    <ConciergeNavItemContainer>
      {({ onClick }) => (
        <ConciergeNavItemDisplay
          minimalistic={minimalistic}
          onClick={() => {
            handleNavClick();
            onClick();
          }}
        />
      )}
    </ConciergeNavItemContainer>
  );
}
